import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, Observable, throwError} from 'rxjs';
import {
  HeaderNavigationLink,
  SubMenuLinkItemEntry,
  SubMenuRow
} from '@app/services/navigation/model/header-navigation-common.model';
import {
  ExploreSapResponse,
  NavigationChild,
  NavigationDataResponse,
  NavigationMenuItem
} from '@app/layout/header/models/navigation-data.model';

@Injectable({
  providedIn: 'root',
})
export class HeaderNavigationDataService {
  private readonly http = inject(HttpClient);
  private readonly isLocalhost = window.location.hostname === 'localhost';

  private readonly mainNavigationUrl = this.isLocalhost
    ? '/sap-main-nav'
    : 'https://www.sap.com/_jcr_content/parHeader/headerstandard.shared.json';

  private readonly exploreNavigationUrl = this.isLocalhost
    ? '/sap-explore-nav'
    : 'https://www.sap.com/bin/sapdxc/cache/header/content/sapdx/countries/en_us/_jcr_content/parHeader/sharedcache/headerstandard.shared.allsap.json';

  getMainNavigationData(): Observable<NavigationDataResponse> {
    return this.http
      .get<NavigationDataResponse>(this.mainNavigationUrl)
      .pipe(catchError(this.handleError('Main Navigation')));
  }

  getExploreSapNavigationData(): Observable<ExploreSapResponse> {
    return this.http
      .get<ExploreSapResponse>(this.exploreNavigationUrl)
      .pipe(catchError(this.handleError('Explore Navigation')));
  }

  transformNavigationData(megaMenu: NavigationMenuItem[]): HeaderNavigationLink[] {
    if (!megaMenu) return [];

    return megaMenu.map((item) => {
      const allItems =
        item.navTitle === 'About' ? this.transformAboutItems(item.children) : this.transformDefaultItems(item.children);

      const subMenuRows = this.chunkIntoRows(allItems, 4);

      if (item.deepLink) {
        subMenuRows.push({
          subMenuLinkItems: [
            {
              category: '',
              url: '',
              items: [
                {
                  title: item.deepLink.navTitle,
                  url: item.deepLink.url,
                },
              ],
              displayItemsAsCategoryLink: false,
            },
          ],
        });
      }

      return {
        title: item.navTitle,
        displayTitle: item.navTitle,
        subMenuRows,
        externalLink: false,
        url: item.url || '',
      };
    });
  }

  transformAboutItems(children: NavigationChild[] = []): SubMenuLinkItemEntry[] {
    return [
      {
        category: '',
        url: '',
        items: children.map((child) => ({
          externalLink: child.target === '_blank',
          title: child.navTitle,
          url: child.url,
        })),
        displayItemsAsCategoryLink: false,
      },
    ];
  }

  transformDefaultItems(children: NavigationChild[] = []): SubMenuLinkItemEntry[] {
    return children.map((child) => ({
      category: child.navTitle,
      url: child.url || '',
      displayItemsAsCategoryLink: false,
      items: this.groupItemsBySubheadings(child.children ?? []),
    }));
  }

  private chunkIntoRows(items: SubMenuLinkItemEntry[], size: number): SubMenuRow[] {
    const rows: SubMenuRow[] = [];
    for (let i = 0; i < items.length; i += size) {
      rows.push({subMenuLinkItems: items.slice(i, i + size)});
    }
    return rows.length ? rows : [{subMenuLinkItems: []}];
  }

  private groupItemsBySubheadings(children: NavigationChild[]): SubMenuLinkItemEntry['items'] {
    const hasSubheadings = children.some((child) => child.itemType === 'subheading');
    if (!hasSubheadings) {
      return children.map((child) => ({
        externalLink: child.target === '_blank',
        title: child.navTitle,
        url: child.url,
      }));
    }

    const result: SubMenuLinkItemEntry['items'] = [];
    let current: SubMenuLinkItemEntry['items'][number] | null = null;
    let currentUsesNestedSubItems = false;

    for (const child of children) {
      if (child.itemType === 'subheading') {
        const nestedSubItems = child.subItems?.map((subItem) => ({title: subItem.navTitle, url: subItem.url})) ?? [];
        currentUsesNestedSubItems = child.subItems !== undefined;
        current = {title: child.navTitle, subItems: nestedSubItems};
        result.push(current);
      } else if (current && !currentUsesNestedSubItems) {
        current.subItems!.push({title: child.navTitle, url: child.url});
      } else {
        result.push({title: child.navTitle, url: child.url});
      }
    }

    return result;
  }

  private handleError(operation: string) {
    return (error: HttpErrorResponse): Observable<never> => {
      const errorMessage = `${operation} failed - Status: ${error.status}, Message: ${
        error.message || 'Unknown Error'
      }`;
      return throwError(() => new Error(errorMessage));
    };
  }
}
